import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Button,
  Chip,
  TableContainer,
  Tooltip,
  IconButton
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FolderAssignment } from '../types';

interface FolderAssignmentsViewProps {
  assignments: FolderAssignment[];
  isLoading: boolean;
  stats: {
    total: number;
    assigned: number;
    unassigned: number;
  };
  onAssign: (assignment: FolderAssignment) => void;
  onUnassign: (assignment: FolderAssignment) => void;
  onRejectSuggestion: (assignment: FolderAssignment) => void;
  onAssignAll: () => void;
  isSubmitting?: boolean;
  isBulkAssigning?: boolean;
  errorMessage?: string | null;
}

const FolderAssignmentsView: React.FC<FolderAssignmentsViewProps> = ({
  assignments,
  isLoading,
  stats,
  onAssign,
  onUnassign,
  onRejectSuggestion,
  onAssignAll,
  isSubmitting = false,
  isBulkAssigning = false,
  errorMessage
}) => {
  const hasProposedAssignments = assignments.some(
    (assignment) => !assignment.isAssigned && Boolean(assignment.suggestedUser)
  );

  const renderAssignmentUsers = (assignment: FolderAssignment) => {
    const { assignedUser, suggestedUser, matchConfidence, matchMethod, requiresReview, matchNotes } = assignment;

    if (!assignedUser && !suggestedUser) {
      return null;
    }

    const getConfidenceChip = (confidence?: number) => {
      if (confidence === undefined || confidence === null) {
        return null;
      }

      const percent = Math.round(confidence * 100);

      if (percent >= 95) {
        return (
          <Chip
            label={`${percent}%`}
            size="small"
            sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
          />
        );
      }

      if (percent >= 85) {
        return (
          <Chip
            label={`${percent}%`}
            size="small"
            sx={{ bgcolor: '#FDD835', color: '#3E2723' }}
          />
        );
      }

      return (
        <Chip
          label={`${percent}%`}
          size="small"
          sx={{ bgcolor: '#FFB74D', color: '#4E342E' }}
        />
      );
    };

    const showSuggested = suggestedUser && (!assignedUser || assignedUser.userId !== suggestedUser.userId);

    return (
      <Box display="flex" flexDirection="column" gap={0.75}>
        {assignedUser && (
          <Box display="flex" flexDirection="column" gap={0.25}>
            <Typography variant="body2" fontWeight={600}>
              {assignedUser.displayName || assignedUser.userId}
            </Typography>
            {assignedUser.email && (
              <Typography variant="caption" color="text.secondary">
                {assignedUser.email}
              </Typography>
            )}
          </Box>
        )}

        {showSuggested && suggestedUser && (
          <Box display="flex" flexDirection="column" gap={0.25}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" fontWeight={600}>
                {suggestedUser.displayName || suggestedUser.userId}
              </Typography>
              {getConfidenceChip(matchConfidence) || (
                <Chip label="Suggested" size="small" color={requiresReview ? 'warning' : 'info'} />
              )}
            </Box>
            {suggestedUser.email && (
              <Typography variant="caption" color="text.secondary">
                {suggestedUser.email}
              </Typography>
            )}
            <Box display="flex" gap={1} alignItems="center">
              {matchMethod && (
                <Chip label={matchMethod} size="small" variant="outlined" />
              )}
              {requiresReview && (
                <Chip label="Review" size="small" color="warning" variant="outlined" />
              )}
            </Box>
            {matchNotes && (
              <Typography variant="caption" color="text.secondary">
                {matchNotes}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderActions = (assignment: FolderAssignment) => {
    const hasAssignment = Boolean(assignment.assignedUser);
    const hasSuggestedOnly = !hasAssignment && Boolean(assignment.suggestedUser);

    return (
      <Box display="flex" gap={1} alignItems="center">
        <Button
          size="small"
          variant="outlined"
          onClick={() => onAssign(assignment)}
          disabled={isSubmitting}
        >
          {hasAssignment ? 'Change Mapping' : hasSuggestedOnly ? 'Map Suggested User' : 'Map to User'}
        </Button>
        {hasAssignment && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => onUnassign(assignment)}
            disabled={isSubmitting}
          >
            Remove
          </Button>
        )}
        {hasSuggestedOnly && (
          <Tooltip title="Dismiss suggestion">
            <span>
              <IconButton
                size="small"
                onClick={() => onRejectSuggestion(assignment)}
                disabled={isSubmitting}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">Folder Assignments</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage SharePoint folder relationships before generating profiles.
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Button
            variant="contained"
            color="primary"
            onClick={onAssignAll}
            disabled={!hasProposedAssignments || isBulkAssigning}
          >
            {isBulkAssigning ? 'Assigning…' : 'Assign All Folders'}
          </Button>
          <Chip label={`Total: ${stats.total}`} size="small" />
          <Chip label={`Assigned: ${stats.assigned}`} color="success" size="small" />
          <Chip label={`Unassigned: ${stats.unassigned}`} color="warning" size="small" />
        </Box>
      </Box>

      {errorMessage && (
        <Box mb={2}>
          <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.light', bgcolor: 'error.50' }}>
            <Typography variant="body2" color="error.main">{errorMessage}</Typography>
          </Paper>
        </Box>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Consultant Type</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>SharePoint Path</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((assignment) => (
                <TableRow key={`${assignment.folder.path}-${assignment.folder.id}`} hover>
                  <TableCell>{assignment.folder.consultantType || 'Unknown'}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {renderAssignmentUsers(assignment) || (
                        <Typography variant="body2" color="text.secondary">
                          {/* empty to preserve row height */}
                          &nbsp;
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{assignment.folder.path}</Typography>
                      {assignment.folder.webUrl && (
                        <Tooltip title="Open in SharePoint">
                          <IconButton
                            size="small"
                            component="a"
                            href={assignment.folder.webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {assignment.isAssigned ? (
                      <Chip label="Assigned" color="success" size="small" />
                    ) : assignment.suggestedUser ? (
                      <Chip label="Proposed" color="info" size="small" />
                    ) : (
                      <Chip label="Unassigned" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">{renderActions(assignment)}</TableCell>
                </TableRow>
              ))}
              {assignments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No consultant folders were found in the latest scan.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>

          {assignments.length > 0 && (
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={onAssignAll}
                disabled={!hasProposedAssignments || isBulkAssigning}
              >
                {isBulkAssigning ? 'Assigning…' : 'Assign All Folders'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default FolderAssignmentsView;
